import { computeDefaultAnchor } from "@semantical/core/modules/diagram/AnchorPosition";
export default class DiagramEdge {
    constructor(modelRelationship, source, target, sourceAnchor, targetAnchor) {
        Object.defineProperty(this, "source", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "target", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "modelRelationship", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "sourceAnchor", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "targetAnchor", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "hidden", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        this.source = source;
        this.target = target;
        this.modelRelationship = modelRelationship;
        this.sourceAnchor = sourceAnchor ?? null;
        this.targetAnchor = targetAnchor ?? null;
    }
    /**
     * Compute absolute position from stored relative anchor (side + offset).
     * Uses the actual dimensions from the DiagramNode.
     */
    computeAbsolutePosition(anchor, diagramNode) {
        const width = diagramNode.width;
        const height = diagramNode.height;
        let x;
        let y;
        switch (anchor.side) {
            case 'top':
                x = diagramNode.x + anchor.offset * width;
                y = diagramNode.y;
                break;
            case 'right':
                x = diagramNode.x + width;
                y = diagramNode.y + anchor.offset * height;
                break;
            case 'bottom':
                x = diagramNode.x + anchor.offset * width;
                y = diagramNode.y + height;
                break;
            case 'left':
                x = diagramNode.x;
                y = diagramNode.y + anchor.offset * height;
                break;
        }
        return { ...anchor, x, y };
    }
    /**
     * Get the source anchor position, computing default if not explicitly set.
     * If an anchor was manually set, recompute absolute position from relative side/offset.
     * Uses actual dimensions from the DiagramNode instances.
     * @param mode - The anchor positioning mode ('auto', 'dynamic', or 'centered')
     * @param ignoreFixed - If true, always compute default anchor (ignore stored fixed position)
     */
    getSourceAnchor(mode = 'auto', ignoreFixed = false) {
        if (this.sourceAnchor && !ignoreFixed) {
            // Recompute absolute position in case the node moved
            return this.computeAbsolutePosition(this.sourceAnchor, this.source);
        }
        return computeDefaultAnchor({ x: this.source.x, y: this.source.y, width: this.source.width, height: this.source.height }, { x: this.target.x, y: this.target.y, width: this.target.width, height: this.target.height }, true, mode);
    }
    /**
     * Get the target anchor position, computing default if not explicitly set.
     * If an anchor was manually set, recompute absolute position from relative side/offset.
     * Uses actual dimensions from the DiagramNode instances.
     * @param mode - The anchor positioning mode ('auto', 'dynamic', or 'centered')
     * @param ignoreFixed - If true, always compute default anchor (ignore stored fixed position)
     */
    getTargetAnchor(mode = 'auto', ignoreFixed = false) {
        if (this.targetAnchor && !ignoreFixed) {
            // Recompute absolute position in case the node moved
            return this.computeAbsolutePosition(this.targetAnchor, this.target);
        }
        return computeDefaultAnchor({ x: this.source.x, y: this.source.y, width: this.source.width, height: this.source.height }, { x: this.target.x, y: this.target.y, width: this.target.width, height: this.target.height }, false, mode);
    }
    /**
     * Update the source anchor position.
     */
    setSourceAnchor(anchor) {
        this.sourceAnchor = anchor;
    }
    /**
     * Update the target anchor position.
     */
    setTargetAnchor(anchor) {
        this.targetAnchor = anchor;
    }
    /**
     * Reset anchors to computed defaults.
     */
    resetAnchors() {
        this.sourceAnchor = null;
        this.targetAnchor = null;
    }
    /**
     * Check if this edge was inferred by the inference engine.
     * Inferred edges are read-only and visually distinct.
     */
    get isInferred() {
        return this.modelRelationship.isInferred;
    }
    /**
     * Reconnect the source end to a new node.
     * Updates both the diagram node reference and the model relationship subject.
     * Resets the source anchor to compute a fresh default position.
     */
    reconnectSource(newNode) {
        this.source = newNode;
        this.modelRelationship.subject = newNode.modelElement;
        this.sourceAnchor = null; // Reset to compute new default
    }
    /**
     * Reconnect the target end to a new node.
     * Updates both the diagram node reference and the model relationship object.
     * Resets the target anchor to compute a fresh default position.
     */
    reconnectTarget(newNode) {
        this.target = newNode;
        this.modelRelationship.object = newNode.modelElement;
        this.targetAnchor = null; // Reset to compute new default
    }
    toJSON() {
        return {
            modelRelationshipRef: this.modelRelationship.id,
            sourceAnchor: this.sourceAnchor,
            targetAnchor: this.targetAnchor
        };
    }
    static fromJSON(json, diagramNodeMap, knowledgeGraph) {
        const relationship = knowledgeGraph.getRelationshipByIdSync(json.modelRelationshipRef);
        if (!relationship) {
            console.debug(`DiagramEdge.fromJSON: Relationship not found for id ${json.modelRelationshipRef}`);
            return null;
        }
        const sourceDiagramNode = diagramNodeMap.get(relationship.subject.uri);
        const targetDiagramNode = diagramNodeMap.get(relationship.object.uri);
        if (!sourceDiagramNode || !targetDiagramNode) {
            console.debug('DiagramEdge.fromJSON: source or target DiagramNode not found');
            return null;
        }
        return new DiagramEdge(relationship, sourceDiagramNode, targetDiagramNode, json.sourceAnchor, json.targetAnchor);
    }
}
